import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export default function ResetPasswordDebugger() {
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [session, setSession] = useState<any>(null);

  useEffect(() => {
    const analyzeUrl = () => {
      const url = window.location.href;
      const hash = window.location.hash;
      const search = window.location.search;
      
      // Extrair tokens de múltiplas formas
      const hashParams = new URLSearchParams(hash.substring(1));
      const queryParams = new URLSearchParams(search);
      
      // Regex patterns
      const accessMatch = url.match(/[#&?]access_token=([^&#]+)/);
      const refreshMatch = url.match(/[#&?]refresh_token=([^&#]+)/);
      const typeMatch = url.match(/[#&?]type=([^&#]+)/);

      setDebugInfo({
        url,
        hash,
        search,
        hashTokens: {
          access_token: hashParams.get('access_token'),
          refresh_token: hashParams.get('refresh_token'),
          type: hashParams.get('type')
        },
        queryTokens: {
          access_token: queryParams.get('access_token'),
          refresh_token: queryParams.get('refresh_token'),
          type: queryParams.get('type')
        },
        regexTokens: {
          access_token: accessMatch ? decodeURIComponent(accessMatch[1]) : null,
          refresh_token: refreshMatch ? decodeURIComponent(refreshMatch[1]) : null,
          type: typeMatch ? decodeURIComponent(typeMatch[1]) : null
        }
      });
    };

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
    };

    analyzeUrl();
    checkSession();
  }, []);

  const testEmailRecovery = async () => {
    const email = prompt('Digite o e-mail para teste:');
    if (!email) return;

    const redirectUrl = `${window.location.origin}/reset-password`;
    console.log('🧪 Testando com redirect URL:', redirectUrl);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });

    if (error) {
      alert(`Erro: ${error.message}`);
      console.error('❌ Erro no teste:', error);
    } else {
      alert(`E-mail enviado! URL de redirect: ${redirectUrl}`);
      console.log('✅ Email enviado com sucesso');
    }
  };

  const showSupabaseConfig = () => {
    const currentOrigin = window.location.origin;
    const productionUrl = 'https://ateris.com.br';
    
    const config = `
PROBLEMA IDENTIFICADO:
O link recebido termina com "redirect_to=https://ateris.com.br/" sem o "/reset-password".
Isso indica que o Supabase está usando a Site URL em vez do redirectTo que passamos.

SOLUÇÃO - CONFIGURAÇÃO NECESSÁRIA NO SUPABASE:

1. ALTERAR SITE URL (CRÍTICO):
   Authentication > Settings > Site URL
   
   MUDE DE: https://ateris.com.br
   PARA: https://ateris.com.br/reset-password
   
   OU se estiver testando localmente:
   ${currentOrigin}/reset-password

2. REDIRECT URLs (Authentication > Settings > Redirect URLs):
   Adicione TODAS essas URLs:
   
   ${currentOrigin}/reset-password
   ${currentOrigin}/auth
   ${productionUrl}/reset-password  
   ${productionUrl}/auth
   
3. VERIFICAR EMAIL TEMPLATE:
   Authentication > Email Templates > Reset Password
   Certifique-se que contém: {{ .RedirectTo }}
   
4. TESTE:
   Após alterar, teste novamente com "Testar Email Recovery"

IMPORTANTE: A Site URL é usada como fallback quando o redirectTo 
não está funcionando corretamente. Por isso o link vem sem /reset-password.
    `;
    
    navigator.clipboard.writeText(config).then(() => {
      alert('Configuração copiada para área de transferência!');
    }).catch(() => {
      console.log(config);
      alert('Configuração impressa no console');
    });
  };

  return (
    <div className="min-h-screen p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Reset Password Debugger</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">URL Analysis</h3>
            <div className="text-sm space-y-1">
              <p><strong>Full URL:</strong> {debugInfo.url}</p>
              <p><strong>Hash:</strong> {debugInfo.hash || 'empty'}</p>
              <p><strong>Search:</strong> {debugInfo.search || 'empty'}</p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Token Detection</h3>
            
            <div className="mb-3">
              <h4 className="font-medium">Hash Parameters:</h4>
              <div className="flex gap-2">
                <Badge variant={debugInfo.hashTokens?.access_token ? "default" : "secondary"}>
                  Access Token: {debugInfo.hashTokens?.access_token ? '✓' : '✗'}
                </Badge>
                <Badge variant={debugInfo.hashTokens?.refresh_token ? "default" : "secondary"}>
                  Refresh Token: {debugInfo.hashTokens?.refresh_token ? '✓' : '✗'}
                </Badge>
                <Badge variant={debugInfo.hashTokens?.type === 'recovery' ? "default" : "secondary"}>
                  Type: {debugInfo.hashTokens?.type || 'none'}
                </Badge>
              </div>
            </div>

            <div className="mb-3">
              <h4 className="font-medium">Query Parameters:</h4>
              <div className="flex gap-2">
                <Badge variant={debugInfo.queryTokens?.access_token ? "default" : "secondary"}>
                  Access Token: {debugInfo.queryTokens?.access_token ? '✓' : '✗'}
                </Badge>
                <Badge variant={debugInfo.queryTokens?.refresh_token ? "default" : "secondary"}>
                  Refresh Token: {debugInfo.queryTokens?.refresh_token ? '✓' : '✗'}
                </Badge>
                <Badge variant={debugInfo.queryTokens?.type === 'recovery' ? "default" : "secondary"}>
                  Type: {debugInfo.queryTokens?.type || 'none'}
                </Badge>
              </div>
            </div>

            <div className="mb-3">
              <h4 className="font-medium">Regex Detection:</h4>
              <div className="flex gap-2">
                <Badge variant={debugInfo.regexTokens?.access_token ? "default" : "secondary"}>
                  Access Token: {debugInfo.regexTokens?.access_token ? '✓' : '✗'}
                </Badge>
                <Badge variant={debugInfo.regexTokens?.refresh_token ? "default" : "secondary"}>
                  Refresh Token: {debugInfo.regexTokens?.refresh_token ? '✓' : '✗'}
                </Badge>
                <Badge variant={debugInfo.regexTokens?.type === 'recovery' ? "default" : "secondary"}>
                  Type: {debugInfo.regexTokens?.type || 'none'}
                </Badge>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Session Status</h3>
            <Badge variant={session ? "default" : "secondary"}>
              {session ? `Logged in as: ${session.user?.email}` : 'Not logged in'}
            </Badge>
          </div>

          <div className="flex gap-2">
            <Button onClick={testEmailRecovery} variant="outline">
              Testar Email Recovery
            </Button>
            <Button onClick={showSupabaseConfig} variant="default">
              Mostrar Configuração do Supabase
            </Button>
          </div>
          
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-2">🔧 Solução do Problema</h4>
            <p className="text-sm text-amber-700 mb-2">
              Os tokens estão ausentes porque a configuração do Supabase não está correta.
            </p>
            <p className="text-sm text-amber-700">
              Clique em "Mostrar Configuração" para ver exatamente o que configurar no painel do Supabase.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}